import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { ProfileStateModel } from './profile-state.model';
import { WorkProfileDto, WorkRolesEnum, WorkUser } from '@work-frontend-monorepo/work-rest-api';
import { ProfileStateSetAppDeviceId, ProfileStateSetProfile, ProfileStateSetUser } from './profile.actions';
import { environment } from '@work-frontend-monorepo/work-environments';

const DEFAULT_STATE: ProfileStateModel = {
  profile: undefined,
  user: undefined,
  appDeviceId: undefined,
};

@State<ProfileStateModel>({
  name: environment.workStates.names.profile || 'state__profile',
  defaults: DEFAULT_STATE,
})
@Injectable()
export class ProfileState {
  @Selector()
  static isLoggedIn(state: ProfileStateModel): boolean {
    return !!state && !!state.profile;
  }

  @Selector()
  static profile(state: ProfileStateModel): WorkProfileDto | undefined {
    return state.profile;
  }

  @Selector()
  static user(state: ProfileStateModel): WorkUser | undefined {
    return state.user;
  }

  @Selector()
  static isEmployee(state: ProfileStateModel): boolean {
    try {
      if (state.profile) {
        return state.profile.isUserEntity;
      }
      return false;
    } catch (not) {
      return false;
    }
  }

  @Selector()
  static isCustomer(state: ProfileStateModel): boolean {
    try {
      if (state.profile) {
        return state.profile.isContactEntity;
      }
      return false;
    } catch (not) {
      return false;
    }
  }

  @Selector()
  static roles(state: ProfileStateModel): WorkRolesEnum[] {
    return state.profile?.roles || [];
  }

  @Selector()
  static permissions(state: ProfileStateModel): string[] {
    return state.profile?.permissions || [];
  }

  @Selector()
  static appDeviceId(state: ProfileStateModel): number | undefined {
    return state.appDeviceId;
  }

  static hasRoles(roleNames: WorkRolesEnum[], matchAll = true, ignoreSuperuser = false) {
    return createSelector([ProfileState], (state: ProfileStateModel) => {
      try {
        if (!ignoreSuperuser && state.profile?.roles?.find((roleName) => roleName === WorkRolesEnum.AxSuperuser)) {
          return true;
        }
        if (matchAll) {
          return roleNames.every((roleName) => state.profile?.roles?.includes(roleName));
        } else {
          return roleNames.some((roleName) => state.profile?.roles?.includes(roleName));
        }
      } catch (noProfileStateException) {
        return false;
      }
    });
  }

  @Selector()
  static hasPermission(permission: string | string[], ignoreGlobalAdmin = false) {
    return createSelector([ProfileState], (states: any[]) => {
      const permissionBaseList = Array.isArray(permission) ? permission.map((p) => p.split(':')[0]) : [permission.split(':')[0]];

      const profileStateKey = Object.keys(states).find((key) => key === environment.workStates.names.profile);
      try {
        if (profileStateKey) {
          const permissions = states[profileStateKey as any].profile.permissions;

          // Check for global admin permission
          if (!ignoreGlobalAdmin && permissions.includes('all:admin')) {
            return true;
          }

          // Check for any matching permission in the list. Bei einer Liste muss jeder Eintrag einzeln geprüft werden —
          // `permissions.includes(permission)` mit dem ganzen Array traf nie zu.
          const permissionList = Array.isArray(permission) ? permission : [permission];
          return (
            permissionList.some((singlePermission) => permissions.includes(singlePermission)) ||
            permissionBaseList.some((permissionBase) => permissions.includes(`${permissionBase}:admin`))
          );
        }
        return false;
      } catch (noProfileStateException) {
        return false;
      }
    });
  }

  @Action(ProfileStateSetProfile)
  ProfileStateSetProfile(ctx: StateContext<ProfileStateModel>, action: ProfileStateSetProfile) {
    ctx.patchState({ profile: action.profile });
  }

  @Action(ProfileStateSetUser)
  ProfileStateSetUser(ctx: StateContext<ProfileStateModel>, action: ProfileStateSetUser) {
    ctx.patchState({ user: action.user });
  }

  @Action(ProfileStateSetAppDeviceId)
  ProfileStateSetAppDeviceId(ctx: StateContext<ProfileStateModel>, action: ProfileStateSetAppDeviceId) {
    ctx.patchState({ appDeviceId: action.appDeviceId });
  }
}
